// tslint:disable:one-line
import {
    IEditorInput, IIEditorInput,
    CloseDirection,  IEditorOpenOptions, EditorOpenPositioning, EditorInput,
    EDITOR_INPUT_ID,
} from '@workbench-stack/core';
import * as _ from 'lodash-es';



const mru: Pick<(IIEditorInput & {editorId?: number}), 'resource' | 'editorId' | 'label'>[] = [];

/**
 * Removes elements from an array and, if necessary, inserts new elements in their place,
 * returning the all elements.
 *
 * @private
 * @param index Location of the array
 * @param del Delete the element in the location
 * @param openedEditors Available Editor Files
 * @param editor Replaces the array position with this editor
 */
function splice(index: number, del: boolean, openedEditors: IEditorInput[], editor?: IEditorInput) {
    const editorToDeleteOrReplace = openedEditors[index];

    const args: (number | IEditorInput)[] = [index, del ? 1 : 0];
    if (editor) {
        args.push(editor);
    }
    // Perform on editors array
    openedEditors.splice.apply(openedEditors, args);

    // Add
    if (!del && editor) {
        mru.push(editor); // make it LRU editor

    }

    // Remove / Replace
    else {
        const indexInMRU = indexOf(editorToDeleteOrReplace, mru);

        // Remove
        if (del && !editor) {
            mru.splice(indexInMRU, 1); // remove from MRU

        }

        // Replace
        else if (del && editor) {
            mru.splice(indexInMRU, 1, editor); // replace MRU at location
        }
    }
    return openedEditors;
}

/**
 * Returns the index of the first occurrence of a editor in an array.
 *
 * @param candidate  — The value to locate in the array.
 * @param editors - Array of the editors
 */
export function indexOf(candidate: EditorInput | null, editors: IEditorInput[]): number {
    if (!candidate) {
        return -1;
    }
    if (editors) {
        for (let i = 0; i < editors.length; i++) {
            // {...candidate,label:candidate.title,name:candidate.title}
            if (matches(editors[i], candidate)) {
                return i;
            }
        }
    }
    return -1;
}

export function getEditors(): IEditorInput[] {
    return mru.slice(0);
}

/**
 * Returns the index of active editor from the editor array.
 *
 * @param editors - Array of the editors
 */
export function indexOfActiveEditor(editors: IEditorInput[]): number {
    if (!editors.length) {
        return -1;
    }
    if (editors) {
        for (let i = 0; i < editors.length; i++) {
            if (editors[i].isActive) {
                return i;
            }
        }
    }
    return -1;
}

/**
 * Returns the index of active editor from the editor array.
 *
 * @param editors - Array of the editors
 */
export function activeEditor(editors: IEditorInput[]): IEditorInput | null {
    if (!editors.length) {
        return;
    }
    if (editors) {
        for (let i = 0; i < editors.length; i++) {
            if (editors[i].isActive) {
                return editors[i];
            }
        }
    }
    return null;
}

/**
 * Returns the index of unpinned(preopened) editor from the editor array.
 * Each editor group will have only one unpinned editor.
 *
 * @param editors - Array of the editors
 */
export function indexOfPreopenEditor(editors: IEditorInput[]): number {
    if (!editors.length) {
        return -1;
    }
    if (editors) {
        for (let i = 0; i < editors.length; i++) {
            if (!editors[i].isPinned) {
                return i;
            }
        }
    }
    return -1;
}

/**
 * Returns true when both editors matches otherwise, false
 *
 * @param editorA
 * @param editorB
 */
export function matches(editorA: EditorInput | null, editorB: EditorInput | null): boolean {
    return !!editorA && !!editorB &&
        // It is possible to have the same resource opened twice (once as a normal input and once as diff input)
        // So we need to compare `label`
        _.isEqual({ resource: editorA.resource, label: editorA.label || (editorA as any).title },
            { resource: editorB.resource, label: editorB.label || (editorB as any).title}); // https://github.com/cdmbase/workbench-stack/issues/259
}

/**
 * Updates the most recent used cache with latest editor.
 *
 * @private
 * @param editor
 * @param editors
 */
function setMostRecentlyUsed(editor: IEditorInput, editors: IEditorInput[]) {
    const index = indexOf(editor, editors);
    if (index === -1) {
        return; // editor not found
    }

    const mruIndex = indexOf(editor, mru);

    // Remove old index
    mru.splice(mruIndex, 1);

    // Set editor to front
    mru.unshift(editor);
}

/**
 * Gets any preopened (or preview) editors which are not pinned. In each editor, there can be
 * only one preopened Editor.
 * @param editors
 * @param editorId
 */
export function preopenEditor(editors: IEditorInput[]) {
    if (!editors.length) {
        return null;
    }
    if (editors) {
        for (let i = 0; i < editors.length; i++) {
            if (!editors[i].isPinned) {
                return editors[i];
            }
        }
    }
    return null;
}


function replaceEditor(
    toReplace: IEditorInput, replaceWith: IEditorInput, editors: IEditorInput[], replaceIndex: number, openNext = true) {

    // flag preceded
    // replaceWith.stats.preceded = true;
    editors = closeEditor(toReplace, editors, { openNext });
    return splice(replaceIndex, false, editors, replaceWith);
}

export function activateEditor(editorInput: EditorInput, editors: IEditorInput[]) {
    const index = indexOf(editorInput, editors);
    if (index < 0) {
        console.warn('(activateEditor) active editor not found!');
        return editors; // editor not found in the list
    }
    const currentActiveIndex = indexOfActiveEditor(editors);
    if (currentActiveIndex !== -1) {
        // deactivate any existing active editor.
        editors[currentActiveIndex] = { ...editors[currentActiveIndex], isActive: false };
    }
    // activate the given editor
    editors[index] = { ...editors[index], isActive: true };
    // Bring to front in MRU list
    setMostRecentlyUsed(editors[index], editors);
    return editors;
}

export function pin(editorInput: EditorInput, editors: IEditorInput[]) {
    const index = indexOf(editorInput, editors);
    if (index === -1) {
        return editors; // not found
    }
    let editor = editors[index];
    if (editor.isPinned) {
        return editors; // can only pin an unpinned editor
    }
    // Convert the preview editor to be a pinned editor
    editors[index] = { ...editors[index], isPinned: true };
    return editors;
}

export function unpin(editorInput: EditorInput, editors: readonly IEditorInput[]) {
    let editorsCopy = editors.slice(0);
    let index = indexOf(editorInput, editorsCopy);
    if (index === -1) {
        return editorsCopy; // not found
    }
    let editor = editors[index];

    if (!editor.isPinned) {
        return editorsCopy; // can only unpin a pinned editor
    }
    const unpinnedEditor = { ...editor, isPinned: false };
    const indexOfPreopen = indexOfPreopenEditor(editorsCopy);
    if (indexOfPreopen !== -1) {
        if (index > indexOfPreopen) {
            index--; // accomodate for the fact that preopen editor closes
        }
        // close the PreOpened
        editorsCopy = closeEditor(editors[indexOfPreopen], editorsCopy, { openNext: false });
        // merge editors
        editorsCopy[index] = { ...unpinnedEditor };
    } else {
        editorsCopy[index] = { ...unpinnedEditor };
    }

    return editorsCopy;
}

export function openEditor(editorInput: IEditorInput, editorOpenedFiles: IEditorInput[],
    options: IEditorOpenOptions = { editorOpenPositioning: EditorOpenPositioning.RIGHT, focusRecentEditorAfterClose: true }) {

    if (options.editorOpenPositioning === undefined) {
        options.editorOpenPositioning = EditorOpenPositioning.RIGHT;
    }
    if (options.focusRecentEditorAfterClose === undefined) {
        options.focusRecentEditorAfterClose = true;
    }
    const preopenedEditor = preopenEditor(editorOpenedFiles);
    // get current index of active editor
    const indexOfActive = indexOfActiveEditor(editorOpenedFiles);
    // get index of the file from the opened list if it exist.
    const index = indexOf(editorInput, editorOpenedFiles);
    let editor = editorOpenedFiles[index] || editorInput;
    const makePinned = options && options.pinned;
    const makeActive = (options && options.active) ||
        // there is no active editor
        (indexOfActive === -1) ||
        // if a different editor is active with preopened editor
        (!makePinned && matches(preopenedEditor, editorOpenedFiles[indexOfActive]));
    // New Editor
    if (index === -1) {
        let targetIndex: number;


        // Insert into specific position
        if (options && typeof options.index === 'number') {
            targetIndex = options.index;
        }

        // Insert to the BEGINNING
        else if (options.editorOpenPositioning === EditorOpenPositioning.FIRST) {
            targetIndex = 0;
        }

        // Insert to the END
        else if (options.editorOpenPositioning === EditorOpenPositioning.LAST) {
            targetIndex = editorOpenedFiles.length;
        }

        // Insert to the LEFT of active editor
        else if (options.editorOpenPositioning === EditorOpenPositioning.LEFT) {
            if (indexOfActive === 0 || !editorOpenedFiles.length) {
                targetIndex = 0; // to the left becoming first editor in list
            } else {
                targetIndex = indexOfActive; // to the left of active editor
            }
        }

        // Insert to the RIGHT of active editor
        else {
            targetIndex = indexOfActive + 1;
        }

        // Insert into our list of editors if pinned or we have no preopen editor
        if (makePinned || !preopenedEditor) {
            editorOpenedFiles = splice(targetIndex, false, editorOpenedFiles, editor);
        }
        // Pin it
        if (makePinned) {
            editorOpenedFiles = pin(editor, editorOpenedFiles);
        }
        // Handle preopend
        if (!makePinned) {

            // Replace existing preopen with this editor if we have a preopen
            if (preopenedEditor) {
                const indexOfPreview = indexOf(preopenedEditor, editorOpenedFiles);
                if (targetIndex > indexOfPreview) {
                    targetIndex--; // accomodate for the fact that preopen editor closes
                }
                editorOpenedFiles = replaceEditor(preopenedEditor, editor, editorOpenedFiles, targetIndex, !makeActive);
            }
            // update the new preview editor.
        }

        // Handle active
        if (makeActive) {
            editorOpenedFiles = activateEditor(editor, editorOpenedFiles);
        }
    }
    // Existing editor
    else {

        // Pin it
        if (makePinned) {
            editorOpenedFiles = pin(editor, editorOpenedFiles);
        }

        // Activate it
        if (makeActive) {
            editorOpenedFiles = activateEditor(editor, editorOpenedFiles);
        }

        // Respect index
        if (options && typeof options.index === 'number') {
            editorOpenedFiles = moveEditor(editor, editorOpenedFiles, options.index);
        }
    }

    return editorOpenedFiles;
}


export function closeEditor(
    editorInput: EditorInput, editors: IEditorInput[],
    options: { openNext?: boolean, focusRecentEditorAfterClose?: boolean } = { openNext: true, focusRecentEditorAfterClose: true }) {
    const index = indexOf(editorInput, editors);
    if (index === -1) {
        return editors;
    }
    let editor = editors[index];
    if (options.focusRecentEditorAfterClose === undefined) {
        options.focusRecentEditorAfterClose = true;
    }
    if (options.openNext === undefined) {
        options.openNext = true;
    }
    // Active Editor closed
    if (options.openNext && editor.isActive) {

        // More than one editor
        if (mru.length > 1) {
            let newActive: IEditorInput;
            if (options.focusRecentEditorAfterClose) {
                newActive = mru[1]; // active editor is always first in MRU, so pick second editor after as new active
            } else {
                if (index === editors.length - 1) {
                    newActive = editors[index - 1]; // last editor is closed, pick previous as new active
                } else {
                    newActive = editors[index + 1]; // pick next editor a new active
                }
            }
            //@sri custom changes for if there is no newActive editor
            if (newActive) {
                editors = activateEditor(newActive, editors);
            }
        }

        // One Editor
        else {
            // editor is empty nothing to do
        }
    }
    // Preview Editor closed
    if (!editor.isPinned) {
        // if not pinned nothing to do
    }
    // Remove from arrays
    return splice(index, true, editors);
}

export function closeEditors(except: EditorInput, editors: IEditorInput[], direction?: CloseDirection) {
    const index = indexOf(except, editors);
    if (index === -1) {
        return editors; // not found
    }
    // Close to the left
    if (direction === CloseDirection.LEFT) {
        for (let i = index - 1; i >= 0; i--) {
            editors = closeEditor(editors[i], editors);
        }
    }
    // Close to the right
    else if (direction === CloseDirection.RIGHT) {
        for (let i = editors.length - 1; i > index; i--) {
            editors = closeEditor(editors[i], editors);
        }
    }
    // Both directions
    else {
        mru.filter(e => !matches(e, except)).forEach(e => {
            editors = closeEditor(e, editors);
        });
    }
    return editors;
}

export function closeAllEditors(editors: IEditorInput[]) {
    const currentActiveIndex = indexOfActiveEditor(editors);
    const currentActiveEditor = editors[currentActiveIndex];
    let openedEditors = editors;
    // to make sure more atleast one in-active editors exist
    if (currentActiveEditor && editors.length > 1) {
        // Optimize: close all non active editors first to produce less upstream work
        mru.filter(e => !matches(e, editors[currentActiveIndex])).forEach(e => {
            openedEditors = closeEditor(e, openedEditors);
        });
    }
    if (currentActiveIndex !== -1) {
        openedEditors = closeEditor(currentActiveEditor, openedEditors);
    }
    return openedEditors;
}

export function moveEditor(editorInput: IEditorInput, editors: IEditorInput[], toIndex: number) {
    const index = indexOf(editorInput, editors);
    // editors = editors ? editors : [];
    editors = editors.map((file) => {
        return {
            ...file,
            isActive: false,
        };
    });
    // let editor = editorInput;
    let editor = editors[index];
    editor = { ...editor, isActive: true };
    //
    // if (index === -1) {
    //     //if editor is not in group
    //
    //     // editors.splice(editors.findIndex(e=>e.id===editor.id),1)
    //     editors.splice(toIndex, 0, editor);
    // }
    // else{
    //     // Move
    //     editors.splice(index, 1);
    //     editors.splice(toIndex, 0, editor);
    // }
    // return editors;

    // const index = indexOf(editorInput, editors);
    if (index === -1) {
        return editors; // editor not found
    }
    // let editor = editors[index];

    // Move
    editors.splice(index, 1);
    editors.splice(toIndex, 0, editor);

    return editors;
}

export function contains(editorOrResource: EditorInput | string, editors: IEditorInput[], supportSideBySide?: boolean) {
    if (typeof editorOrResource !== 'string') {
        const index = indexOf(editorOrResource, editors);
        if (index >= 0) {
            return true;
        }

        if (supportSideBySide && editorOrResource.typeId === EDITOR_INPUT_ID.SIDE_BY_SIDE_EDITOR_INPUT) {
            const sideBySideIndex = indexOf(editorOrResource, editors);  // same as normal editorinput nothing special about sidebyside
            if (sideBySideIndex >= 0) {
                return true;
            }

            return false;
        }
    }
    const matchedEditors = editors.filter(ed => ed.resource === editorOrResource);
    return !!matchedEditors.length;
}
